/**
 * HelperText component for form field descriptions and error messages
 *
 * Styled with Tailwind CSS for dark theme.
 * Supports different states (default, error, success, warning).
 */

import type { HelperTextOptions } from '../types/form';

/**
 * Base CSS classes for the helper text element
 */
const BASE_CLASSES = 'text-xs leading-normal';

/**
 * CSS classes for different text types
 */
const TYPE_CLASSES: Record<NonNullable<HelperTextOptions['type']>, string> = {
  default: 'text-muted-foreground',
  error: 'text-destructive',
  success: 'text-success',
  warning: 'text-warning',
};

/**
 * Create a HelperText element
 */
export function createHelperText(options: HelperTextOptions): HTMLParagraphElement {
  const {
    text,
    type = 'default',
    className = '',
    attributes = {},
  } = options;

  const paragraph = document.createElement('p');

  // Set CSS classes
  const typeClasses = TYPE_CLASSES[type];
  paragraph.className = `${BASE_CLASSES} ${typeClasses} ${className}`.trim();

  // Set text content
  paragraph.textContent = text;

  // Set ARIA attributes for accessibility
  if (type === 'error') {
    paragraph.setAttribute('role', 'alert');
    paragraph.setAttribute('aria-live', 'polite');
  }

  // Set additional attributes
  for (const [key, val] of Object.entries(attributes)) {
    paragraph.setAttribute(key, val);
  }

  return paragraph;
}

/**
 * HelperText class for object-oriented usage
 */
export class HelperText {
  private element: HTMLParagraphElement;
  private options: HelperTextOptions;

  constructor(options: HelperTextOptions) {
    this.options = { ...options };
    this.element = createHelperText(this.options);
  }

  /**
   * Get the element
   */
  getElement(): HTMLParagraphElement {
    return this.element;
  }

  /**
   * Set the text content
   */
  setText(text: string): this {
    this.options.text = text;
    this.element.textContent = text;
    return this;
  }

  /**
   * Set the type (affects styling)
   */
  setType(type: NonNullable<HelperTextOptions['type']>): this {
    this.options.type = type;
    const typeClasses = TYPE_CLASSES[type];

    // Remove all type classes
    Object.values(TYPE_CLASSES).forEach((classes) => {
      classes.split(' ').forEach((cls) => {
        this.element.classList.remove(cls);
      });
    });

    // Add new type classes
    typeClasses.split(' ').forEach((cls) => {
      this.element.classList.add(cls);
    });

    // Update ARIA attributes
    if (type === 'error') {
      this.element.setAttribute('role', 'alert');
      this.element.setAttribute('aria-live', 'polite');
    } else {
      this.element.removeAttribute('role');
      this.element.removeAttribute('aria-live');
    }

    return this;
  }

  /**
   * Show the helper text
   */
  show(): this {
    this.element.classList.remove('hidden');
    return this;
  }

  /**
   * Hide the helper text
   */
  hide(): this {
    this.element.classList.add('hidden');
    return this;
  }

  /**
   * Check if visible
   */
  isVisible(): boolean {
    return !this.element.classList.contains('hidden');
  }
}
